import { CHAT_URL } from '../restEndpoint';
import { httpGet, httpPost, httpDelete, httpPostStream, httpPostBlob } from '../../utils/httpMethods';
import * as Sentry from "@sentry/react";

const { logger } = Sentry;

// Get chat history for a specific thread
export const getChatHistory = async (userId, organizationId, threadId) => {
  return await httpGet({
    url: `${CHAT_URL}/history/`,
    params: { userId, organizationId, threadId }
  });
};

// Get all threads for user (with pagination support)
export const getThreads = async (userId, organizationId, limit = 50, skip = 0) => {
  return await httpGet({
    url: `${CHAT_URL}/threads`,
    params: {
      userId,
      organizationId,
      limit,
      skip
    }
  });
};

// Create new thread
// For anonymous users, userId can be undefined/null - backend will generate one from session
export const createThread = async (userId, organizationId) => {
  return await httpPost({
    url: `${CHAT_URL}/create-thread`,
    payload: { 
      ...(userId && { userId }), // Only include userId if it exists
      ...(organizationId && { organizationId }) // Only include organizationId if it exists
    }
  });
};

// Chat with model (now includes threadId)
export const chatWithModel = async (payload) => {
  return await httpPost({
    url: `${CHAT_URL}/model`,
    payload
  });
};

// Delete thread
export const deleteThread = async (userId, threadId) => {
  return await httpDelete({
    url: `${CHAT_URL}/delete-thread`,
    payload: { userId, threadId }
  });
};

// Delete all threads
export const deleteAllThreads = async (userId, organizationId) => {
  return await httpDelete({
    url: `${CHAT_URL}/delete-all-threads`,
    payload: { userId, organizationId }
  });
};

// Streaming regenerate (ChatGPT-style)
export const regenerateMessageStream = async ({ userId, threadId, messageId, modelId, onMessage, abortController }) => {
  return await httpPostStream({
    url: `${CHAT_URL}/regenerate`,
    payload: { userId, threadId, messageId, modelId },
    onMessage,
    abortController,
  });
};

// Image generation
export const generateImage = async (payload, file = null) => {
  // If file is provided, use FormData (multipart/form-data)
  if (file) {
    logger.debug('[generateImage] File provided, using FormData', {
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size
    });
    const formData = new FormData();
    formData.append('message', payload.message);
    formData.append('userId', payload.userId);
    if (payload.organizationId) formData.append('organizationId', payload.organizationId);
    if (payload.threadId) formData.append('threadId', payload.threadId);
    if (payload.modelId) formData.append('modelId', payload.modelId);
    // Use 'inputImage' field name as expected by backend route
    formData.append('inputImage', file);
    
    // Use httpPostBlob for multipart/form-data
    return await httpPostBlob({
      url: `${CHAT_URL}/image-generation`,
      payload: formData,
    });
  } else {
    logger.debug('[generateImage] No file provided, using JSON POST');
    // No file, use regular JSON POST
    return await httpPost({
      url: `${CHAT_URL}/image-generation`,
      payload,
    });
  }
};

// Enhanced prompt API
export const enhancePrompt = async (prompt) => {
  return await httpPost({
    url: `${CHAT_URL}/enhanced-promt`,
    payload: { prompt }
  });
};